import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { BRAND } from "@/lib/data";
import { FOOTER } from "@/constants/testIds";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid={FOOTER.root}
            className="bg-[#111111] text-[#F8F8F6] px-6 md:px-12 pt-20 pb-10"
        >
            <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
                {/* Logo + tagline */}
                <div className="md:col-span-5">
                    <img
                        src={BRAND.logo}
                        alt="The Treasure Threads"
                        className="h-32 md:h-36 w-auto"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                    <p className="font-serif-display text-2xl md:text-3xl mt-8 max-w-md leading-snug text-[#F8F8F6]">
                        A heritage emporium of Asia&rsquo;s most exceptional handcrafted treasures.
                    </p>
                    <div className="hairline-gold mt-8" />
                    <p className="mt-6 text-sm text-[#BBBBBB] max-w-sm font-light">
                        {BRAND.parent} · Established {BRAND.founded}
                    </p>
                </div>

                {/* Quick links */}
                <div className="md:col-span-3">
                    <p className="overline text-[#D4AF37] mb-6">Discover</p>
                    <ul className="space-y-3">
                        <li>
                            <Link to="/" data-testid={FOOTER.linkHome} className="text-[#F8F8F6] hover:text-[#D4AF37] transition-colors">
                                Home
                            </Link>
                        </li>
                        <li>
                            <Link to="/about" data-testid={FOOTER.linkAbout} className="text-[#F8F8F6] hover:text-[#D4AF37] transition-colors">
                                About
                            </Link>
                        </li>
                        <li>
                            <Link to="/collections" data-testid={FOOTER.linkCollections} className="text-[#F8F8F6] hover:text-[#D4AF37] transition-colors">
                                Collections
                            </Link>
                        </li>
                        <li>
                            <a
                                href={buildWhatsAppUrl("Hello The Treasure Threads, I would like to enquire about your collection.")}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={FOOTER.linkWhatsapp}
                                className="text-[#F8F8F6] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
                            >
                                <MessageCircle className="w-4 h-4" strokeWidth={1.4} /> WhatsApp
                            </a>
                        </li>
                    </ul>
                </div>

                {/* Contact */}
                <div className="md:col-span-4">
                    <p className="overline text-[#D4AF37] mb-6">Visit &amp; Enquire</p>
                    <ul className="space-y-4 text-sm text-[#F8F8F6] font-light">
                        <li className="flex items-start gap-3" data-testid={FOOTER.address}>
                            <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                            <span>{BRAND.address}</span>
                        </li>
                        <li className="flex items-center gap-3" data-testid={FOOTER.phone}>
                            <Phone className="w-4 h-4 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                            <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-[#D4AF37] transition-colors">
                                {BRAND.phone}
                            </a>
                        </li>
                        <li className="flex items-center gap-3" data-testid={FOOTER.email}>
                            <Mail className="w-4 h-4 flex-shrink-0 text-[#D4AF37]" strokeWidth={1.4} />
                            <a href={`mailto:${BRAND.email}`} className="hover:text-[#D4AF37] transition-colors break-all">
                                {BRAND.email}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto mt-16 pt-8 border-t border-[#2A2A2A] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <p className="text-xs text-[#888888] font-light tracking-wide">
                    &copy; {year} {BRAND.parent}. All rights reserved.
                </p>
                <p className="text-xs text-[#888888] font-light tracking-widest uppercase">
                    Curated in Chiang Mai · Delivered Worldwide
                </p>
            </div>
        </footer>
    );
}
